import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, X, ClipboardList } from "lucide-react";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKDataTable, VLKStatusBadge, VLKRiskMeter, VLKButton,
  VLKAlertDialog, VLKTextarea, VLKLabel, toast, VLKEmptyState,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";

export default function ApprovalQueue() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["institution-requests"],
    queryFn: () => api.get("/institution/requests").then(r => r.data),
  });
  const [decision, setDecision] = useState(null); // {request, kind}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async (kind) => {
    setBusy(true);
    try {
      await api.post(`/institution/requests/${decision.request.request_id}/decide`, { decision: kind, note });
      toast.success(`Request ${kind.toLowerCase()}d`, { description: decision.request.institution_name });
      setDecision(null); setNote("");
      qc.invalidateQueries({ queryKey: ["institution-requests"] });
      qc.invalidateQueries({ queryKey: ["kpis"] });
    } catch (e) { toast.error("Failed", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <VLKPageHeader
        title="Institution approval queue"
        description="Multi-party compliance review — approvals require compliance_officer or system_admin."
      />

      <VLKDataTable
        columns={[
          { key: "institution_name", header: "Institution", render: (r) => <span className="font-medium">{r.institution_name}</span> },
          { key: "type", header: "Type", render: (r) => <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{r.type}</span> },
          { key: "country", header: "Country", mono: true },
          { key: "contact_email", header: "Contact", render: (r) => <span className="font-mono text-xs">{r.contact_email}</span> },
          { key: "requested_scopes", header: "Scopes", render: (r) => (r.requested_scopes || []).map(s => <span key={s} className="font-mono text-[10px] uppercase text-muted-foreground border border-border px-1.5 py-0.5 rounded-[2px] mr-1">{s}</span>) },
          { key: "risk_level", header: "Risk", render: (r) => <VLKRiskMeter level={r.risk_level} /> },
          { key: "status", header: "Status", render: (r) => <VLKStatusBadge status={r.status.toLowerCase()}>{r.status}</VLKStatusBadge> },
          { key: "actions", header: "", align: "right", render: (r) =>
              r.status === "PENDING" ? (
                <div className="flex gap-1 justify-end">
                  <VLKButton data-testid={IDS.approvalApprove(r.request_id)} size="sm" variant="primary" onClick={() => { setDecision({ request: r, kind: "APPROVE" }); setNote(""); }}>
                    <Check className="h-3.5 w-3.5" />Approve
                  </VLKButton>
                  <VLKButton data-testid={IDS.approvalReject(r.request_id)} size="sm" variant="outline" onClick={() => { setDecision({ request: r, kind: "REJECT" }); setNote(""); }}>
                    <X className="h-3.5 w-3.5" />Reject
                  </VLKButton>
                </div>
              ) : <span className="text-xs text-muted-foreground font-mono">—</span>
          },
        ]}
        rows={data?.items || []}
        rowKey={(r) => r.request_id}
        rowTestId={(r) => IDS.approvalRow(r.request_id)}
        empty={<VLKEmptyState icon={ClipboardList} title="Approval queue is clear" description="No pending institution requests." />}
      />

      <VLKAlertDialog
        open={!!decision}
        onOpenChange={() => setDecision(null)}
        title={`${decision?.kind === "APPROVE" ? "Approve" : "Reject"} ${decision?.request?.institution_name || ""}`}
        tone={decision?.kind === "REJECT" ? "destructive" : "default"}
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setDecision(null)}>Cancel</VLKButton>
            <VLKButton variant={decision?.kind === "REJECT" ? "destructive" : "primary"} onClick={() => decide(decision.kind)} loading={busy}>
              Confirm {decision?.kind?.toLowerCase()}
            </VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-sm">This decision is recorded to the immutable audit ledger.</div>
          <div className="space-y-1.5">
            <VLKLabel>Review note (optional)</VLKLabel>
            <VLKTextarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Compliance rationale…" />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}
